export const INTERVIEW = {
  /** Default hard cap when difficulty is unknown (medium). */
  MAX_DURATION_SEC: 60 * 60,
  /** Default per-question time budget when the AI does not specify one. */
  DEFAULT_QUESTION_SEC: 90,
  /** How often the candidate camera is snapshotted (kept for the record). */
  SNAPSHOT_INTERVAL_MS: 12_000,
  /** How often a near-live frame is pushed to the manager (overwrites). */
  LIVE_FRAME_INTERVAL_MS: 1_500,
  /** How often the manager live view polls for updates. */
  LIVE_POLL_INTERVAL_MS: 1_800,
  /** Number of focus-loss violations before the interview is flagged. */
  VIOLATION_THRESHOLD: 3,
  /** Default question count when difficulty is unknown (medium). */
  QUESTION_COUNT: 40,
} as const;

/** Interview question difficulty chosen when creating a job. */
export type QuestionDifficulty = "simple" | "medium" | "advanced";

export const QUESTION_COUNT_BY_DIFFICULTY: Record<QuestionDifficulty, number> = {
  simple: 20,
  medium: 40,
  advanced: 60,
};

/** Overall interview time budget by difficulty (~90s average per question). */
export const MAX_DURATION_BY_DIFFICULTY: Record<QuestionDifficulty, number> = {
  simple: 35 * 60,
  medium: 60 * 60,
  advanced: 90 * 60,
};

export function normalizeQuestionDifficulty(
  raw?: string | null,
): QuestionDifficulty {
  const v = String(raw || "medium").trim().toLowerCase();
  if (v === "simple" || v === "easy") return "simple";
  if (v === "advanced" || v === "hard" || v === "difficult") return "advanced";
  return "medium";
}

export function questionCountForDifficulty(raw?: string | null): number {
  return QUESTION_COUNT_BY_DIFFICULTY[normalizeQuestionDifficulty(raw)];
}

export function maxDurationForDifficulty(raw?: string | null): number {
  return MAX_DURATION_BY_DIFFICULTY[normalizeQuestionDifficulty(raw)];
}

export type InterviewStatus =
  | "invited"
  | "in_progress"
  | "completed"
  | "violated"
  | "expired";

/** Structured answer types only — free text is intentionally not offered. */
export type QuestionType =
  | "mcq"
  | "multi"
  | "boolean"
  | "select"
  | "scale"
  | "text";

/**
 * Interview questions are organised into clear classifications so the manager
 * can review them grouped by theme. `text` from older interviews is tolerated.
 */
export const QUESTION_CATEGORIES = [
  "experience",
  "technical",
  "problem_solving",
  "behavioral",
  "situational",
  "motivation",
  "logistics",
  "wellbeing",
] as const;
export type QuestionCategory = (typeof QUESTION_CATEGORIES)[number] | "role" | "hr";

export type EventType =
  | "join"
  | "leave"
  | "blur"
  | "focus"
  | "violation"
  | "snapshot"
  | "voice"
  | "answer"
  | "finish";

export const RECOMMENDATIONS = [
  "strong_hire",
  "hire",
  "maybe",
  "no_hire",
  "strong_no_hire",
] as const;
export type Recommendation = (typeof RECOMMENDATIONS)[number];

/** Manager's hiring decision on a candidate, independent of interview status. */
export const DECISIONS = ["shortlisted", "accepted", "rejected"] as const;
export type Decision = (typeof DECISIONS)[number];

export const RECOMMENDATION_LABELS: Record<Recommendation, string> = {
  strong_hire: "Strong Hire",
  hire: "Hire",
  maybe: "Maybe",
  no_hire: "No Hire",
  strong_no_hire: "Strong No Hire",
};
